import React, { useEffect, useState } from "react";
import CodeBlock from "./CodeBlock";
import { BASE, AUTH_EXAMPLE, groups, comingSoon } from "./content";

// Public API docs (star-hire style): sticky header, scroll-spy sidebar,
// light/dark toggle. Self-contained, themed via scoped CSS vars under `.emdocs`.

const STORAGE_KEY = "em-docs-theme";

// Flat, in-document-order list of every anchored section (for the sidebar + scroll-spy).
const SECTIONS = [
  { id: "introduction", title: "Introduction" },
  { id: "authentication", title: "Authentication" },
  ...groups.flatMap((g) => g.endpoints.map((ep) => ({ id: ep.id, title: ep.title }))),
  { id: "coming-soon", title: "Coming soon" }
];
const LAST_ID = SECTIONS[SECTIONS.length - 1].id;

const STYLES = `
.emdocs { --bg:#ffffff; --fg:#1a2226; --muted:#5c676c; --border:#e6ebec; --sidebar:#8a9499; --accent:#0f9d82; --header-bg:rgba(255,255,255,.9); --pill-bg:#f6ddda; --pill-fg:#b5352c; }
.emdocs.dark { --bg:#0f1416; --fg:#e6edf0; --muted:#9aa4a9; --border:#243036; --sidebar:#7c878c; --accent:#2fd6b0; --header-bg:rgba(15,20,22,.9); --pill-bg:rgba(221,93,93,.20); --pill-fg:#f0938d; }
.emdocs { background:var(--bg); color:var(--fg); min-height:100vh; font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif; }
.emdocs a { color:inherit; text-decoration:none; }
.emdocs code { font-family:"SFMono-Regular",Menlo,Consolas,monospace; background:transparent; color:inherit; padding:0; border:0; }
.emdocs-p code { background:var(--pill-bg); color:var(--pill-fg); padding:2px 6px; border-radius:4px; font-size:.88em; }
.emdocs-header { position:sticky; top:0; z-index:10; background:var(--header-bg); -webkit-backdrop-filter:blur(8px); backdrop-filter:blur(8px); border-bottom:1px solid var(--border); }
.emdocs-header-inner { display:flex; align-items:center; justify-content:space-between; padding:14px 24px; }
.emdocs-brand { font-weight:600; font-size:18px; }
.emdocs-ver { font-size:13px; color:var(--sidebar); margin-right:14px; }
.emdocs-toggle { background:transparent; border:1px solid var(--border); color:var(--fg); border-radius:6px; width:34px; height:34px; cursor:pointer; font-size:15px; line-height:1; }
.emdocs-toggle:hover { border-color:var(--accent); color:var(--accent); }
.emdocs-layout { display:flex; gap:40px; padding:0 24px; max-width:1200px; margin:0 auto; }
.emdocs-nav { width:224px; flex-shrink:0; padding:40px 0; position:sticky; top:64px; align-self:flex-start; max-height:calc(100vh - 64px); overflow-y:auto; }
.emdocs-navlink { display:block; padding:4px 0 4px 12px; margin-left:-1px; font-size:13.5px; border-left:2px solid transparent; color:var(--muted); }
.emdocs-navlink:hover { color:var(--accent); }
.emdocs-navlink.active { border-left-color:var(--accent); color:var(--accent); font-weight:600; }
.emdocs-navgroup { margin-top:16px; }
.emdocs-navgroup-label { font-size:11px; text-transform:uppercase; letter-spacing:.5px; color:var(--sidebar); margin-bottom:4px; padding-left:12px; }
.emdocs-main { flex:1; min-width:0; padding:40px 0 80px; }
.emdocs-section { margin-bottom:44px; }
.emdocs-section, .emdocs-article { scroll-margin-top:88px; }
.emdocs-article { margin-bottom:44px; }
.emdocs-h1 { font-size:30px; font-weight:700; margin:0 0 12px; }
.emdocs-h2 { font-size:22px; font-weight:600; margin:0 0 18px; padding-bottom:8px; border-bottom:1px solid var(--border); }
.emdocs-h3 { font-size:17px; font-weight:600; margin:12px 0 4px; }
.emdocs-p { color:var(--muted); line-height:1.6; margin:0 0 12px; }
.emdocs-badge { display:inline-block; min-width:52px; text-align:center; font-size:12px; font-weight:600; padding:2px 8px; border-radius:4px; margin-right:10px; }
.emdocs-badge.GET { background:#d7f5ec; color:#0f7a63; } .emdocs.dark .emdocs-badge.GET { background:rgba(16,163,127,.22); color:#5fe3c4; }
.emdocs-badge.POST { background:#dce9fb; color:#2563a8; } .emdocs.dark .emdocs-badge.POST { background:rgba(59,130,246,.22); color:#8fbcf9; }
.emdocs-badge.PATCH { background:#faeccd; color:#a9741a; } .emdocs.dark .emdocs-badge.PATCH { background:rgba(217,153,57,.22); color:#e6b968; }
.emdocs-badge.DELETE { background:#f8dcda; color:#b5352c; } .emdocs.dark .emdocs-badge.DELETE { background:rgba(221,93,93,.22); color:#f0938d; }
.emdocs-eppath { font-size:14px; font-weight:600; }
.emdocs-table { width:100%; border-collapse:collapse; font-size:13.5px; margin-bottom:14px; border:1px solid var(--border); border-radius:6px; overflow:hidden; }
.emdocs-table th { text-align:left; font-weight:600; padding:8px 12px; background:var(--border); }
.emdocs-table td { padding:8px 12px; border-top:1px solid var(--border); color:var(--muted); vertical-align:top; }
.emdocs-table td code { color:var(--fg); }
.emdocs-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.emdocs-soon { display:inline-block; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.5px; padding:2px 8px; border-radius:4px; background:var(--border); color:var(--sidebar); margin-right:10px; }
.emdocs-soonrow { display:flex; align-items:center; color:var(--muted); margin-bottom:8px; }
@media (max-width:1024px) { .emdocs-nav { display:none; } .emdocs-grid { grid-template-columns:1fr; } }
`;

const MethodBadge = ({ method }) => <span className={`emdocs-badge ${method}`}>{method}</span>;

const Endpoint = ({ ep }) => (
  <article id={ep.id} className="emdocs-article">
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
      <MethodBadge method={ep.method} />
      <code className="emdocs-eppath">{ep.path}</code>
    </div>
    <h3 className="emdocs-h3">{ep.title}</h3>
    <p className="emdocs-p">{ep.description}</p>

    {ep.params && ep.params.length > 0 && (
      <table className="emdocs-table">
        <thead>
          <tr><th>Parameter</th><th>Type</th><th>Description</th></tr>
        </thead>
        <tbody>
          {ep.params.map((p) => (
            <tr key={p.name}>
              <td><code>{p.name}</code>{p.required && <span style={{ color: "#dd5d5d" }}> *</span>}</td>
              <td>{p.type}</td>
              <td>{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}

    <div className="emdocs-grid">
      <CodeBlock label="Request" code={ep.request} />
      <CodeBlock label="Response" code={ep.response} />
    </div>
  </article>
);

const DocsNav = ({ activeId, setActiveId }) => {
  const linkClass = (id) => `emdocs-navlink${activeId === id ? " active" : ""}`;
  return (
    <nav className="emdocs-nav">
      <a href="#introduction" className={linkClass("introduction")} onClick={() => setActiveId("introduction")}>Introduction</a>
      <a href="#authentication" className={linkClass("authentication")} onClick={() => setActiveId("authentication")}>Authentication</a>
      {groups.map((group) => (
        <div key={group.id} className="emdocs-navgroup">
          <div className="emdocs-navgroup-label">{group.title}</div>
          {group.endpoints.map((ep) => (
            <a key={ep.id} href={`#${ep.id}`} className={linkClass(ep.id)} onClick={() => setActiveId(ep.id)}>{ep.title}</a>
          ))}
        </div>
      ))}
      <div className="emdocs-navgroup">
        <a href="#coming-soon" className={linkClass("coming-soon")} onClick={() => setActiveId("coming-soon")}>Coming soon</a>
      </div>
    </nav>
  );
};

const prefersDark = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

const Developers = ({ match }) => {
  const expected = process.env.REACT_APP_DEVELOPERS_SECRET;
  const provided = match && match.params && match.params.secret;
  const authorized = expected && provided === expected;

  const [isDark, setIsDark] = useState(false);
  const [activeId, setActiveId] = useState("introduction");

  // Seed theme from the docs' own storage, falling back to OS preference.
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    setIsDark(stored ? stored === "dark" : prefersDark());
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isDark ? "dark" : "light");
  }, [isDark]);

  // Scroll-spy: highlight the section sitting in the top band of the viewport.
  useEffect(() => {
    if (!authorized) return;

    if (window.location.hash) setActiveId(window.location.hash.slice(1));

    const atBottom = () =>
      window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

    const observer = new IntersectionObserver(
      (entries) => {
        if (atBottom()) { setActiveId(LAST_ID); return; }
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-88px 0px -66% 0px", threshold: 0 }
    );

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    const onScroll = () => { if (atBottom()) setActiveId(LAST_ID); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { observer.disconnect(); window.removeEventListener("scroll", onScroll); };
  }, [authorized]);

  // The docs live outside the login gate; a shared secret in the URL is the
  // only thing protecting them.
  if (!authorized) {
    return (
      <div style={{ textAlign: "center", padding: "80px 20px", fontFamily: "sans-serif" }}>
        <h2>404</h2>
        <p style={{ color: "#8a9499" }}>Not found.</p>
      </div>
    );
  }

  return (
    <div className={`emdocs${isDark ? " dark" : ""}`}>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      <header className="emdocs-header">
        <div className="emdocs-header-inner">
          <div className="emdocs-brand">Emerson Coaching API</div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className="emdocs-ver">v1</span>
            <button className="emdocs-toggle" onClick={() => setIsDark((d) => !d)} title="Toggle theme">
              {isDark ? "☀" : "☾"}
            </button>
          </div>
        </div>
      </header>

      <div className="emdocs-layout">
        <DocsNav activeId={activeId} setActiveId={setActiveId} />

        <main className="emdocs-main">
          <section id="introduction" className="emdocs-section">
            <h1 className="emdocs-h1">API Reference</h1>
            <p className="emdocs-p">
              The Emerson Coaching REST API lets you read your pipeline and perform common actions —
              list deals and users, change stages, add notes, pull transcripts, and read reports.
              All endpoints return JSON and are served under a single base URL.
            </p>
            <CodeBlock label="Base URL" code={BASE} />
          </section>

          <section id="authentication" className="emdocs-section">
            <h2 className="emdocs-h2">Authentication</h2>
            <p className="emdocs-p">
              Authenticate every request by sending your API key as a bearer token in the{" "}
              <code>Authorization</code> header. Create and manage keys under{" "}
              <strong>Settings &rarr; API Keys</strong> in the dashboard. Keep them secret.
            </p>
            <CodeBlock label="curl" code={AUTH_EXAMPLE} />
            <p className="emdocs-p" style={{ marginTop: 8 }}>
              Requests without a valid key return <code>401 Unauthorized</code>. The API is rate
              limited to 100 requests per minute per key; exceeding it returns{" "}
              <code>429 Too Many Requests</code>.
            </p>
            <p className="emdocs-p" style={{ marginTop: 8 }}>
              Each key is either <strong>read-only</strong> (GET requests only) or{" "}
              <strong>read/write</strong>. A read-only key returns <code>403 Forbidden</code> on any
              write (POST). Your key&rsquo;s scope is shown in the <code>GET /me</code> response.
            </p>
          </section>

          {groups.map((group) => (
            <section key={group.id} className="emdocs-section">
              <h2 className="emdocs-h2">{group.title}</h2>
              {group.endpoints.map((ep) => <Endpoint key={ep.id} ep={ep} />)}
            </section>
          ))}

          <section id="coming-soon" className="emdocs-section">
            <h2 className="emdocs-h2">More endpoints coming soon</h2>
            <p className="emdocs-p">We&rsquo;re expanding the API. These resources are on the roadmap:</p>
            {comingSoon.map((item) => (
              <div key={item} className="emdocs-soonrow">
                <span className="emdocs-soon">Soon</span>{item}
              </div>
            ))}
          </section>
        </main>
      </div>
    </div>
  );
};

export default Developers;
